import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

/* ═══════════════════════════════════════════════════════════════════════════
   SendPlaylist — Setlist Pages for DJs
   Dark, ultra-minimal landing page. One idea. No noise.
   ═══════════════════════════════════════════════════════════════════════════ */

// ── Animation primitives ─────────────────────────────────────────────────────

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.unobserve(el)
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold])

  return { ref, visible }
}

function Reveal({
  children,
  className = '',
  delay = 0,
  direction = 'up',
}: {
  children: React.ReactNode
  className?: string
  delay?: number
  direction?: 'up' | 'down' | 'left' | 'right' | 'none'
}) {
  const { ref, visible } = useInView()

  const offsets: Record<string, string> = {
    up: 'translateY(32px)',
    down: 'translateY(-32px)',
    left: 'translateX(32px)',
    right: 'translateX(-32px)',
    none: 'none',
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : offsets[direction],
        transition: `opacity 800ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 800ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  )
}

// ── Ambient glow ─────────────────────────────────────────────────────────────

function Orb({
  color,
  size,
  style,
}: {
  color: string
  size: number
  style?: React.CSSProperties
}) {
  return (
    <div
      className="pointer-events-none absolute rounded-full"
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 40% 40%, ${color}, transparent 70%)`,
        filter: 'blur(80px)',
        ...style,
      }}
    />
  )
}

// ── Setlist preview card ──────────────────────────────────────────────────────

function SetlistCard({
  artist,
  city,
  date,
  tracks,
  featured,
  visible,
  delay = 0,
}: {
  artist: string
  city: string
  date: string
  tracks: string[]
  featured?: string
  visible: boolean
  delay?: number
}) {
  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(24px)',
        transition: `opacity 700ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 700ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs tracking-[0.2em] uppercase mb-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {city} · {date}
          </p>
          <h3 className="text-white font-semibold text-lg tracking-tight">{artist}</h3>
        </div>

        {/* Tracks */}
        <div className="px-6 py-4 space-y-3">
          {tracks.map((track, i) => (
            <div key={i} className="flex items-center gap-3">
              <span
                className="text-xs font-mono w-4 text-right shrink-0"
                style={{ color: 'rgba(255,255,255,0.2)' }}
              >
                {i + 1}
              </span>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {track}
              </span>
            </div>
          ))}
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono w-4 text-right shrink-0" style={{ color: 'rgba(255,255,255,0.1)' }}>
              ···
            </span>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
              +18 more tracks
            </span>
          </div>
        </div>

        {/* Featured */}
        {featured && (
          <div
            className="mx-4 mb-4 px-4 py-3 rounded-xl flex items-center gap-3"
            style={{ background: 'rgba(255,140,0,0.08)', border: '1px solid rgba(255,140,0,0.15)' }}
          >
            <span style={{ color: 'rgba(255,140,0,0.8)' }}>🔥</span>
            <div>
              <p className="text-xs mb-0.5" style={{ color: 'rgba(255,140,0,0.5)' }}>
                Featured Track
              </p>
              <p className="text-sm font-medium" style={{ color: 'rgba(255,200,100,0.9)' }}>
                {featured}
              </p>
            </div>
          </div>
        )}

        {/* Listen buttons */}
        <div className="px-4 pb-5 flex gap-2">
          {['Spotify', 'Apple Music', 'YouTube'].map((platform) => (
            <button
              key={platform}
              className="flex-1 py-2 rounded-lg text-xs font-medium tracking-wide transition-all duration-200"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.5)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)'
                e.currentTarget.style.color = 'rgba(255,255,255,0.9)'
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                e.currentTarget.style.color = 'rgba(255,255,255,0.5)'
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'
              }}
            >
              {platform}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── URL preview ───────────────────────────────────────────────────────────────

function UrlPill({ path, delay = 0, visible }: { path: string; delay?: number; visible: boolean }) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full px-4 py-2"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(12px)',
        transition: `opacity 600ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 600ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
        sendplaylist.com
      </span>
      <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
        {path}
      </span>
    </div>
  )
}

// ── Pricing card ──────────────────────────────────────────────────────────────

function PricingCard({
  plan,
  price,
  sub,
  features,
  highlight,
  delay = 0,
}: {
  plan: string
  price: string
  sub: string
  features: string[]
  highlight?: boolean
  delay?: number
}) {
  return (
    <Reveal delay={delay}>
      <div
        className="rounded-2xl p-8 flex flex-col h-full"
        style={{
          background: highlight ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
          border: highlight ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="mb-8">
          <p
            className="text-xs tracking-[0.2em] uppercase mb-4"
            style={{ color: highlight ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.25)' }}
          >
            {plan}
          </p>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-4xl font-bold tracking-tight" style={{ color: highlight ? '#fff' : 'rgba(255,255,255,0.7)' }}>
              {price}
            </span>
            {sub && (
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {sub}
              </span>
            )}
          </div>
        </div>

        <ul className="space-y-3 flex-1 mb-8">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0" style={{ color: highlight ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)' }}>
                —
              </span>
              <span className="text-sm" style={{ color: highlight ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)' }}>
                {f}
              </span>
            </li>
          ))}
        </ul>

        <button
          className="w-full py-3 rounded-xl text-sm font-semibold tracking-wide transition-all duration-200"
          style={
            highlight
              ? { background: '#fff', color: '#000' }
              : { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }
          }
          onMouseEnter={(e) => {
            if (!highlight) {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)'
              e.currentTarget.style.color = 'rgba(255,255,255,0.8)'
            } else {
              e.currentTarget.style.background = 'rgba(255,255,255,0.9)'
            }
          }}
          onMouseLeave={(e) => {
            if (!highlight) {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
              e.currentTarget.style.color = 'rgba(255,255,255,0.5)'
            } else {
              e.currentTarget.style.background = '#fff'
            }
          }}
        >
          {highlight ? 'Start free trial' : 'Get started'}
        </button>
      </div>
    </Reveal>
  )
}

// ── Use-case row ──────────────────────────────────────────────────────────────

function UseCaseRow({
  number,
  title,
  body,
  delay = 0,
}: {
  number: string
  title: string
  body: string
  delay?: number
}) {
  return (
    <Reveal delay={delay}>
      <div className="flex gap-8 py-8" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <span
          className="text-sm font-mono shrink-0 mt-0.5"
          style={{ color: 'rgba(255,255,255,0.15)' }}
        >
          {number}
        </span>
        <div>
          <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {body}
          </p>
        </div>
      </div>
    </Reveal>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TestPage() {
  const [heroLoaded, setHeroLoaded] = useState(false)
  const [demoVisible, setDemoVisible] = useState(false)
  const demoRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setHeroLoaded(true), 80)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    const el = demoRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setDemoVisible(true)
          observer.unobserve(el)
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const heroStyle = (delay: number): React.CSSProperties => ({
    opacity: heroLoaded ? 1 : 0,
    transform: heroLoaded ? 'none' : 'translateY(20px)',
    transition: `opacity 900ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 900ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
  })

  return (
    <div
      className="relative min-h-screen overflow-x-hidden"
      style={{
        background: '#080808',
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        color: '#fff',
      }}
    >
      {/* ── Background ambience ─────────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <Orb color="rgba(120,80,255,0.18)" size={900} style={{ top: '-200px', left: '-200px' }} />
        <Orb color="rgba(255,80,120,0.10)" size={700} style={{ top: '30%', right: '-150px' }} />
        <Orb color="rgba(80,200,255,0.06)" size={600} style={{ bottom: '10%', left: '20%' }} />
        {/* Subtle grid */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`,
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between"
        style={{
          background: 'rgba(8,8,8,0.7)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <span className="text-sm font-semibold tracking-tight text-white">SendPlaylist</span>
        <div className="flex items-center gap-4">
          <a
            href="#how-it-works"
            className="text-xs tracking-wide transition-colors duration-200"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)' }}
          >
            How it works
          </a>
          <a
            href="#pricing"
            className="text-xs tracking-wide transition-colors duration-200"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)' }}
          >
            Pricing
          </a>
          <Link
            to="/login"
            className="text-xs tracking-wide transition-colors duration-200"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)' }}
          >
            Log in
          </Link>
          <a
            href="#pricing"
            className="px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all duration-200"
            style={{ background: '#fff', color: '#000' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.85)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff' }}
          >
            Get started
          </a>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative pt-40 pb-32 px-6 flex flex-col items-center text-center">
        {/* Eyebrow */}
        <div style={heroStyle(0)}>
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-10"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs tracking-[0.15em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Now in early access
            </span>
          </div>
        </div>

        {/* Headline */}
        <div style={heroStyle(80)}>
          <h1
            className="font-bold leading-[0.95] tracking-[-0.04em] mb-8"
            style={{
              fontSize: 'clamp(56px, 10vw, 112px)',
              letterSpacing: '-0.04em',
            }}
          >
            <span className="block text-white">Share your</span>
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.4) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              DJ setlists.
            </span>
          </h1>
        </div>

        {/* Sub */}
        <div style={heroStyle(160)}>
          <p
            className="text-lg max-w-md mb-12 leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            Turn every set into a shareable music page.
            Fans find the tracks. Artists get discovered.
          </p>
        </div>

        {/* CTA */}
        <div style={heroStyle(240)} className="flex flex-col sm:flex-row items-center gap-3">
          <a
            href="#pricing"
            className="px-8 py-4 rounded-full text-sm font-semibold tracking-wide transition-all duration-300"
            style={{ background: '#fff', color: '#000' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.9)'
              e.currentTarget.style.transform = 'scale(1.02)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff'
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Start free
          </a>
          <a
            href="#how-it-works"
            className="px-8 py-4 rounded-full text-sm font-semibold tracking-wide transition-all duration-300"
            style={{
              background: 'rgba(255,255,255,0.04)',
              color: 'rgba(255,255,255,0.6)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)'
              e.currentTarget.style.color = 'rgba(255,255,255,0.9)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
              e.currentTarget.style.color = 'rgba(255,255,255,0.6)'
            }}
          >
            See an example
          </a>
        </div>

        {/* URL preview */}
        <div style={heroStyle(320)} className="mt-8">
          <UrlPill path="/anyma-toronto" delay={0} visible={true} />
        </div>

        {/* Scroll hint */}
        <div style={heroStyle(400)} className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div
            className="flex flex-col items-center gap-2"
            style={{ color: 'rgba(255,255,255,0.15)' }}
          >
            <span className="text-xs tracking-[0.2em] uppercase">Scroll</span>
            <div
              className="w-px h-8"
              style={{
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.2), transparent)',
                animation: 'pulse 2s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </section>

      {/* ── Live demo setlist ─────────────────────────────────────────── */}
      <section id="how-it-works" className="relative px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="mb-4 flex items-center gap-3">
              <span
                className="text-xs tracking-[0.2em] uppercase"
                style={{ color: 'rgba(255,255,255,0.25)' }}
              >
                Example page
              </span>
              <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
            </div>
          </Reveal>

          <Reveal delay={60}>
            <p className="text-xs mb-8" style={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace' }}>
              sendplaylist.com/anyma-toronto
            </p>
          </Reveal>

          {/* Cards */}
          <div ref={demoRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <SetlistCard
              artist="ANYMA"
              city="Toronto"
              date="March 18"
              tracks={['Follow You – Anyma, Rebüke', 'Explore – Anyma', 'Losers – Anyma & Cassian']}
              featured="LAIS – Locked Down"
              visible={demoVisible}
              delay={0}
            />
            <SetlistCard
              artist="John Summit"
              city="Chicago"
              date="February 22"
              tracks={['La Femme – John Summit', 'In Chicago – John Summit', 'Dance All Night – John Summit']}
              visible={demoVisible}
              delay={120}
            />
            <SetlistCard
              artist="Dom Dolla"
              city="New York"
              date="January 10"
              tracks={['Eat Your Man – Dom Dolla', 'Pump the Brakes – Dom Dolla', 'San Frandisco – Dom Dolla']}
              featured="Fisher – Atmosphere"
              visible={demoVisible}
              delay={240}
            />
          </div>
        </div>
      </section>

      {/* ── The problem ───────────────────────────────────────────────── */}
      <section className="relative px-6 py-28">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase mb-12" style={{ color: 'rgba(255,255,255,0.2)' }}>
              The problem
            </p>
          </Reveal>

          <Reveal delay={60}>
            <h2
              className="font-bold mb-12 leading-tight tracking-tight"
              style={{ fontSize: 'clamp(32px, 5vw, 52px)', color: 'rgba(255,255,255,0.85)' }}
            >
              After every set, fans ask the same question.
            </h2>
          </Reveal>

          <div className="space-y-4 mb-16">
            {[
              { text: '"tracklist?"', delay: 120 },
              { text: '"what song was that at 43min?"', delay: 180 },
              { text: '"setlist??"', delay: 240 },
              { text: '"ID on track 7 plz"', delay: 300 },
            ].map(({ text, delay }) => (
              <Reveal key={text} delay={delay}>
                <div
                  className="inline-block rounded-full px-5 py-2.5"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span className="text-base" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>
                    {text}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={360}>
            <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Right now DJs point fans to YouTube comments, Reddit threads, or 1001Tracklists.
              The experience is broken.{' '}
              <span style={{ color: 'rgba(255,255,255,0.7)' }}>There's no clean standard.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Divider ─────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6">
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent)' }} />
      </div>

      {/* ── Use cases ─────────────────────────────────────────────────── */}
      <section className="relative px-6 py-28">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase mb-16" style={{ color: 'rgba(255,255,255,0.2)' }}>
              Why DJs use it
            </p>
          </Reveal>

          <UseCaseRow
            number="01"
            title="Show your taste"
            body="Your setlist is your signature. Every track you play tells fans who you are. Now there's a place for that story to live."
            delay={60}
          />
          <UseCaseRow
            number="02"
            title="Promote artists you love"
            body="Pin a featured track. Give spotlight to the emerging artists in your set. They'll remember it."
            delay={120}
          />
          <UseCaseRow
            number="03"
            title="Give fans a way to relive the night"
            body="One link. All the music. Fans open Spotify or Apple Music and the whole set is right there."
            delay={180}
          />
          <UseCaseRow
            number="04"
            title="Own your discovery"
            body="Your setlists index on search. Fans looking for Anyma setlists, John Summit setlists, Solomun setlists — they land on your page."
            delay={240}
          />
        </div>
      </section>

      {/* ── Divider ─────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6">
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent)' }} />
      </div>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section className="relative px-6 py-28">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase mb-16" style={{ color: 'rgba(255,255,255,0.2)' }}>
              How it works
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Add your tracks',
                body: 'Search and add every track from your set. Takes less than 5 minutes.',
                delay: 0,
              },
              {
                step: '2',
                title: 'Get your link',
                body: 'You get a clean URL. sendplaylist.com/your-set-name. Share it everywhere.',
                delay: 100,
              },
              {
                step: '3',
                title: 'Fans listen instantly',
                body: 'One tap opens the full setlist on Spotify, Apple Music, or YouTube.',
                delay: 200,
              },
            ].map(({ step, title, body, delay }) => (
              <Reveal key={step} delay={delay}>
                <div>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mb-5 text-xs font-semibold"
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    {step}
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Divider ─────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6">
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent)' }} />
      </div>

      {/* ── Pricing ──────────────────────────────────────────────────── */}
      <section id="pricing" className="relative px-6 py-28">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase mb-4" style={{ color: 'rgba(255,255,255,0.2)' }}>
              Pricing
            </p>
          </Reveal>
          <Reveal delay={60}>
            <h2
              className="font-bold mb-16 tracking-tight"
              style={{ fontSize: 'clamp(28px, 4vw, 44px)', color: 'rgba(255,255,255,0.85)' }}
            >
              Start free. Go pro when you need it.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PricingCard
              plan="Free"
              price="$0"
              sub="forever"
              features={[
                '1 setlist page',
                'Spotify, Apple Music & YouTube links',
                'SendPlaylist branding',
              ]}
              delay={0}
            />
            <PricingCard
              plan="Pro"
              price="$12"
              sub="/mo"
              features={[
                'Unlimited setlist pages',
                'Custom branding & domain',
                'Featured track placement',
                'Analytics & fan insights',
                'Upcoming shows section',
              ]}
              highlight
              delay={100}
            />
          </div>
        </div>
      </section>

      {/* ── Divider ─────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6">
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent)' }} />
      </div>

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section className="relative px-6 py-40 text-center overflow-hidden">
        <Orb color="rgba(120,80,255,0.14)" size={700} style={{ top: '-100px', left: '50%', transform: 'translateX(-50%)' }} />

        <Reveal>
          <h2
            className="font-bold tracking-tight mb-6"
            style={{
              fontSize: 'clamp(40px, 7vw, 88px)',
              lineHeight: '0.95',
              letterSpacing: '-0.04em',
            }}
          >
            <span className="block" style={{ color: 'rgba(255,255,255,0.85)' }}>Your next set</span>
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.3) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              deserves a page.
            </span>
          </h2>
        </Reveal>

        <Reveal delay={80}>
          <p className="text-base mb-12 max-w-sm mx-auto" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Free to start. Takes 5 minutes. Fans will thank you.
          </p>
        </Reveal>

        <Reveal delay={160}>
          <a
            href="#pricing"
            className="inline-block px-10 py-4 rounded-full text-sm font-semibold tracking-wide transition-all duration-300"
            style={{ background: '#fff', color: '#000' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.88)'
              e.currentTarget.style.transform = 'scale(1.03)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff'
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Create your setlist page →
          </a>
        </Reveal>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer
        className="px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4"
        style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
      >
        <span className="text-sm font-semibold tracking-tight text-white">SendPlaylist</span>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
          Also works for playlists.
        </p>
        <div className="flex items-center gap-6">
          <Link
            to="/privacy-policy"
            className="text-xs transition-colors duration-200"
            style={{ color: 'rgba(255,255,255,0.2)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.2)' }}
          >
            Privacy
          </Link>
          <Link
            to="/terms-of-service"
            className="text-xs transition-colors duration-200"
            style={{ color: 'rgba(255,255,255,0.2)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.2)' }}
          >
            Terms
          </Link>
        </div>
      </footer>
    </div>
  )
}
